import { useEffect, useState, useCallback } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Receipt, AlertTriangle, RefreshCw } from "lucide-react";
import { http, fmtEUR } from "@/lib/api";

/**
 * Pie chart «Σήμερα: Ταμείο ΑΑ vs ΒΒ» — visualises today's split between
 * fiscal (cash register / Z-report) receipts and off-register cash from
 * the ΣΥΝΕΧΙΣΗ ΕΙΣΠΡΑΞΗΣ flow. Polled-once on mount; refresh button on
 * the header for ad-hoc reload. A warning chip lights up when BB exceeds
 * ~30 % of the day's total (manager spec).
 */
const COLOR_AA = "#059669"; // emerald-600
const COLOR_BB = "#f59e0b"; // amber-500

export default function CashDrawersPanel() {
  const [data, setData] = useState({ aa_total: 0, bb_total: 0, total: 0, aa_breakdown: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data: d } = await http.get("/cash-drawers/today");
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Αποτυχία φόρτωσης");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const total = data.total || 0;
  const aaPct = total > 0 ? (data.aa_total / total) * 100 : 0;
  const bbPct = total > 0 ? (data.bb_total / total) * 100 : 0;
  const bbWarn = bbPct > 30 && total > 0;

  const pieData = total > 0
    ? [
        { name: "Ταμειακή (φορολογικά)", value: data.aa_total, color: COLOR_AA },
        { name: "Απλές παραγγελίες", value: data.bb_total, color: COLOR_BB },
      ].filter((d) => d.value > 0)
    : [];

  return (
    <Card className="p-5" data-testid="cash-drawers-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Receipt size={18} className="text-indigo-600" />
          <h4 className="font-bold text-lg">Ταμείο — Σήμερα</h4>
          {bbWarn && (
            <span
              className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-[11px] font-bold"
              data-testid="cash-drawers-bb-warn"
            >
              <AlertTriangle size={11} /> {bbPct.toFixed(0)}%
            </span>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          className="h-8 border-2"
          data-testid="cash-drawers-refresh"
        >
          <RefreshCw size={13} className={`mr-1 ${loading ? "animate-spin" : ""}`} />
          Ανανέωση
        </Button>
      </div>

      {err && (
        <div className="p-3 mb-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
          {err}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* LEFT — totals + per-method AA breakdown */}
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-lg border-2 border-slate-300 bg-slate-50 px-3 py-2 text-center">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                Συνολικό
              </div>
              <div
                className="text-xl font-bold font-mono text-slate-800 mt-0.5"
                data-testid="cash-drawers-total"
              >
                {fmtEUR(total)}
              </div>
            </div>
            <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 px-3 py-2 text-center">
              <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">
                Ταμειακή
              </div>
              <div
                className="text-xl font-bold font-mono text-emerald-700 mt-0.5"
                data-testid="cash-drawers-aa"
              >
                {fmtEUR(data.aa_total)}
              </div>
              <div className="text-[10px] text-emerald-700 mt-0.5">{aaPct.toFixed(0)}%</div>
            </div>
            <div className="rounded-lg border-2 border-amber-300 bg-amber-50 px-3 py-2 text-center">
              <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">
                Απλές Παρ.
              </div>
              <div
                className="text-xl font-bold font-mono text-amber-700 mt-0.5"
                data-testid="cash-drawers-bb"
              >
                {fmtEUR(data.bb_total)}
              </div>
              <div className="text-[10px] text-amber-700 mt-0.5">{bbPct.toFixed(0)}%</div>
            </div>
          </div>

          {/* AA per-method breakdown */}
          {data.aa_breakdown?.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">
                Ανάλυση ανά τρόπο πληρωμής
              </div>
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md bg-white">
                {data.aa_breakdown.map((row) => (
                  <li
                    key={row.method_name}
                    className="flex items-center justify-between px-3 py-1.5 text-sm"
                    data-testid={`cash-drawers-method-${row.method_name}`}
                  >
                    <span className="font-medium text-slate-700">{row.method_name}</span>
                    <span className="font-mono font-bold text-slate-900">{fmtEUR(row.amount)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* RIGHT — pie chart */}
        <div className="min-h-[220px]">
          {total > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={85}
                  paddingAngle={2}
                  isAnimationActive={false}
                  label={({ value }) => fmtEUR(value)}
                >
                  {pieData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} stroke="#fff" strokeWidth={2} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => fmtEUR(Number(v))} />
                <Legend verticalAlign="bottom" iconType="circle" />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] grid place-items-center text-slate-400 text-sm border-2 border-dashed border-slate-200 rounded-lg">
              Καμία είσπραξη σήμερα
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
